import { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import {
  IconLoader,
  Table,
  useQueryState,
  useScroll,
  useFilters,
  Filters,
  formatDate,
  usePrevious,
  SelectItemProps,
  useGetList,
  SchemaFilterType,
  PermissionDenied,
  PermissionEnum,
  formatPhone,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import { clearApplicationsList } from './store/slices';
import {
  newApplicationListColumns,
  newApplicationListFiltersSchema,
  newApplicationListColumnsLabels,
  newApplicationListSchema,
} from './variables/list';
import {
  ApplicationType,
  NewApplicationListItem,
  NewApplicationStatus,
  ReviewStatus,
} from './types';
import { useLazyGetNewApplicationsListQuery } from './store/services';
import getApplicationStatus from './helpers/get-status-name';
import { clearNewApplicationInfoState } from './individual/store/slices';
import { clearNewApplicationInfoState as clearNewCorporateApplicationState } from './corporate/store/slices';
import { clearNewApplicationInfoState as clearNewMRApplicationState } from './main-representative/store/slices';
import cn from './new-application.module.css';

const formatNewApplicationsList = (
  list: NewApplicationListItem[],
): NewApplicationListItem[] => {
  const nextList = list?.map((item) => {
    const result = { ...item };

    let statusClassName = '';
    let reviewStatus = '';
    let type = '';

    if (result.status === NewApplicationStatus.ACCEPTED) {
      statusClassName = cn.newApplicationStatusAccepted;
    } else if (result.status === NewApplicationStatus.INCOMPLETE) {
      statusClassName = cn.newApplicationStatusIncomplete;
    } else if (result.status === NewApplicationStatus.DECLINED) {
      statusClassName = cn.newApplicationStatusDeclined;
    }

    if (result.type === ApplicationType.INDIVIDUAL) {
      type = 'Individual';
    } else if (result.type === ApplicationType.CORPORATE) {
      type = 'Corporate';
    } else if (result.type === ApplicationType.COMPANY_OFFICIAL) {
      type = 'Company official';
    }

    if (result.reviewStatus === ReviewStatus.NONE) {
      reviewStatus = 'None';
    } else if (result.reviewStatus === ReviewStatus.ACCEPTED) {
      reviewStatus = 'Accepted';
    } else if (result.reviewStatus === ReviewStatus.HAS_DUPLICATE) {
      statusClassName = cn.newApplicationStatusDeclined;
      reviewStatus = 'Has Duplicate';
    } else if (result.reviewStatus === ReviewStatus.REJECTED) {
      reviewStatus = 'Rejected';
    }

    const render: Record<string, React.JSX.Element> = {
      id: <div className={cn.applicationDocId}>{result.id}</div>,
      createdAt: <div>{formatDate(result.createdAt)}</div>,
      phone: <div>{formatPhone(result.phone) || '-'}</div>,
      firstName: (
        <div>
          {result.type === ApplicationType.INDIVIDUAL ||
          result.type === ApplicationType.COMPANY_OFFICIAL
            ? result.firstName || '-'
            : '-'}
        </div>
      ),
      lastName: (
        <div>
          {result.type === ApplicationType.INDIVIDUAL ||
          result.type === ApplicationType.COMPANY_OFFICIAL
            ? result.lastName || '-'
            : '-'}
        </div>
      ),
      companyName: (
        <div>
          {result.type === ApplicationType.CORPORATE
            ? `${result.companyName}`
            : '-'}
        </div>
      ),
      countryId: (
        <div className={cn.applicationCountry}>
          {result.country ? result.country.name : '-'}
        </div>
      ),
      reviewEmployeeId: (
        <div>
          {result.reviewEmployeeId
            ? `${result?.reviewEmployee?.profile?.firstName} ${result?.reviewEmployee?.profile?.lastName}`
            : '-'}
        </div>
      ),
      status: (
        <div
          className={`${statusClassName} ${cn.newApplicationStatus} ${cn.applicationStatus}`}
        >
          {`${getApplicationStatus(result.status)} ${
            result.reviewStatus === ReviewStatus.HAS_DUPLICATE
              ? `(${reviewStatus})`
              : ''
          }`}
        </div>
      ),
      email: (
        <div className={cn.newApplicationEmail}>{result.email || '-'}</div>
      ),
      type: <div className={cn.newApplicationType}>{type}</div>,
    };

    result.render = render;

    return result;
  });

  return nextList;
};

const NewApplcationList = () => {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const {
    root: { userId },
    newApplications: {
      list: { newApplications, pagination },
      individualInfo: { newApplicationInfo },
      corporateInfo: { newApplicationInfo: newCorporateApplicationInfo },
      mainRepresentative: { newApplicationInfo: newMRApplicationInfo },
    },
    customersDictionaries: {
      countries,
      dictionariesIsLoading,
      activePermissions,
    },
  } = useAppSelector((state) => state);

  const [formattedColumns, setFormattedColumns] = useState(
    newApplicationListColumns,
  );

  const [savedFilters, setSavedFilters] = useState<
    {
      id: string;
      name: string;
      value: Record<string, any>;
      default?: boolean;
      employeeId: string;
    }[]
  >([]);
  const [selectedSavedFilter, setSelectedSavedFilter] = useState('');

  const dispatch = useAppDispatch();
  const { search } = useLocation();

  const { filters, sort, onSortChange, onFilterChange } = useQueryState(
    newApplicationListSchema,
  );

  const prevFilters = usePrevious(filters);

  const [getCustomersApplicationsList, { isFetching, isLoading }] =
    useLazyGetNewApplicationsListQuery();

  const { getListAction, onSort, onFilter, setPage, errorList } =
    useGetList<NewApplicationListItem>({
      getList: getCustomersApplicationsList,
      onFilterChange,
      onSortChange,
      schema: newApplicationListSchema,
    });

  const { filtersComponent, setValues, handleSubmit } = useFilters({
    filtersSchema: newApplicationListFiltersSchema,
    filters,
    onFilterChange: onFilter,
  });

  const formattedData = useMemo(() => {
    const data = formatNewApplicationsList(
      newApplications as NewApplicationListItem[],
    );

    return data;
  }, [newApplications, filters, sort]);

  const onSavedFilterClick = (id: string, savedFilter: Filters) => {
    const filterValues: Filters = {
      ...savedFilter,
      take: { value: ['20'], type: SchemaFilterType.STRING },
    };

    onFilter(filterValues);
    setSelectedSavedFilter(id);
  };

  useScroll({ element: scrollRef, requestInProgress: isFetching });

  useEffect(() => {
    setValues('countryId', countries as SelectItemProps[]);
  }, [countries]);

  useEffect(() => {
    dispatch(clearNewApplicationInfoState());
    dispatch(clearNewCorporateApplicationState());
    dispatch(clearNewMRApplicationState());
  }, [newApplicationInfo, newCorporateApplicationInfo, newMRApplicationInfo]);

  useEffect(() => {
    let nextColumns = ([] as any[]).concat(newApplicationListColumns);

    if (filters.type) {
      if (filters.type.value) {
        nextColumns = nextColumns.map((item) => {
          const result = { ...item };

          if (!filters.type.value.includes('CORPORATE')) {
            if (result.name === 'companyName') {
              result.hidden = true;
            }
          } else if (
            filters.type.value.length === 1 &&
            filters.type.value.includes('CORPORATE')
          ) {
            if (
              result.name === 'firstName' ||
              result.name === 'lastName' ||
              result.name === 'phone' ||
              result.name === 'email'
            ) {
              result.hidden = true;
            }
          }

          return result;
        });
      }

      setFormattedColumns(nextColumns);
    } else if (filters.companyName) {
      nextColumns = nextColumns.map((item) => {
        const result = { ...item };

        if (
          result.name === 'firstName' ||
          result.name === 'lastName' ||
          result.name === 'phone' ||
          result.name === 'email'
        ) {
          result.hidden = true;
        }

        return result;
      });

      setFormattedColumns(nextColumns);
    } else {
      setFormattedColumns(newApplicationListColumns);
    }
  }, [filters]);

  useEffect(() => {
    if (
      prevFilters &&
      !Object.keys(prevFilters).length &&
      filters &&
      !newApplications &&
      sort &&
      sort.sortProp
    ) {
      let nextFilters: Filters = {};

      const savedFiltersJSON = localStorage.getItem('applications-filters');

      let parsed: Record<string, any> = {};

      if (search) {
        parsed = queryString.parse(search);

        if (parsed) {
          delete parsed.sortProp;
          delete parsed.sortDir;
        }
      }

      let parsedSavedFilters: {
        id: string;
        name: string;
        value: Record<string, any>;
        default?: boolean;
      }[];

      let defaultSavedFilter;

      if (!Object.keys(filters)?.length) {
        if (savedFiltersJSON) {
          parsedSavedFilters = JSON.parse(savedFiltersJSON);

          if (parsedSavedFilters && parsedSavedFilters?.length) {
            const currentUserParsedFilters = parsedSavedFilters?.filter(
              (item: any) => item.employeeId === userId,
            );

            if (currentUserParsedFilters && currentUserParsedFilters?.length) {
              defaultSavedFilter = currentUserParsedFilters?.find(
                (item) => item.default,
              );
            }
          }
        }

        if (defaultSavedFilter) {
          setSelectedSavedFilter(defaultSavedFilter.id);

          nextFilters = { ...nextFilters, ...defaultSavedFilter?.value };
        } else {
          nextFilters = { ...nextFilters, ...filters };
        }
      } else {
        nextFilters = { ...nextFilters, ...filters };
      }

      if (
        !filters.status &&
        !defaultSavedFilter &&
        !Object.keys(parsed as any).length
      ) {
        nextFilters.status = {
          value: [
            NewApplicationStatus.READY_TO_REVIEW,
            NewApplicationStatus.REVIEW_IN_PROGRESS,
          ],
          type: SchemaFilterType.STRING,
        };
      }

      getListAction({ filters: nextFilters, sort });
    }
  }, [filters, sort, newApplications, search]);

  useEffect(() => {
    dispatch(clearApplicationsList());
  }, []);

  useEffect(() => {
    const savedFiltersJSON = localStorage.getItem('applications-filters');

    if (savedFiltersJSON) {
      const parsed = JSON.parse(savedFiltersJSON);

      if (parsed) {
        const currentEmployeeSavedFilters = parsed?.filter(
          (item: any) => item.employeeId === userId,
        );

        setSavedFilters(currentEmployeeSavedFilters);
      }
    }
  }, [userId]);

  let content: React.JSX.Element;

  if (isLoading || dictionariesIsLoading) {
    content = (
      <div className={cn.newApplicationLoader}>
        <IconLoader />
      </div>
    );
  } else if (
    !isLoading &&
    countries &&
    activePermissions?.includes(PermissionEnum.APPLICATIONS_CUSTOMERS_LIST)
  ) {
    content = (
      <Table
        title="New applications"
        columns={formattedColumns}
        data={formattedData || []}
        onSort={(name) => {
          onSort(name);
        }}
        error={errorList}
        filters={filters}
        sort={sort}
        columnsLabels={newApplicationListColumnsLabels}
        onFilterChange={(values) => {
          setSelectedSavedFilter('');

          onFilter({ ...values });
        }}
        filtersComponent={filtersComponent}
        handleSubmit={handleSubmit}
        isLoading={isFetching}
        hiddedAllFilterTags={Boolean(selectedSavedFilter)}
        take="20"
        setPage={(data) => {
          setPage({ ...data, filters });
        }}
        savedFilters={savedFilters}
        setSavedFilters={setSavedFilters}
        savedFiltersKey="applications-filters"
        selectedSavedFilter={selectedSavedFilter}
        onSavedFilterClick={onSavedFilterClick}
        setSelectedSavedFilter={setSelectedSavedFilter}
        userId={userId}
        pagination={pagination}
        withScroll
        clickable
      />
    );
  } else {
    content = <PermissionDenied />;
  }

  return (
    <div className={cn.newApplicationBottom} ref={scrollRef}>
      {content}
    </div>
  );
};

export default NewApplcationList;
